import React, { useState, useEffect, useCallback } from "react";
// Switched to adminAxios to handle /api prefix and security headers automatically
import adminAxios from "./adminAxios"; 
import { useTheme } from "../context/ThemeContext";
import socket from "../socket"; 

const PaymentAudit = () => {
  const { colors } = useTheme();
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("pending"); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Retrieve isolated tenant context identifier
  const tenantId = localStorage.getItem("adminId") || "global";

  /**
   * SYNC DATA: Fetches logs based on the active sector.
   * Endpoints updated to include 'admin' to pass through authMiddleware.js regex.
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = activeTab === "pending" 
        ? "transactions/admin/pending" 
        : "transactions/admin/all-history";
        
      const res = await adminAxios.get(endpoint, { params: { tenantId } });
      
      if (activeTab === "pending") {
        setRequests(Array.isArray(res.data) ? res.data : []);
      } else {
        setHistory(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      console.error("[AUDIT SYNC ERROR]:", err.response?.data || err.message);
      setError(`STATION ERROR: Could not sync ${activeTab} logs.`);
    } finally {
      setLoading(false);
    }
  }, [activeTab, tenantId]);

  /**
   * REAL-TIME LISTENERS
   */
  useEffect(() => {
    fetchData();

    socket.emit("join", { userId: "admin", role: "admin", tenantId });

    socket.on("new_payment_submitted", fetchData);
    socket.on("admin_status_update", fetchData);

    return () => {
      socket.off("new_payment_submitted", fetchData);
      socket.off("admin_status_update", fetchData);
    };
  }, [fetchData, tenantId]);

  /**
   * ACTION HANDLER: Authorizes or Rejects a node deposit.
   */
  const handleAction = async (id, status) => {
    const finalStatus = status === 'settled' ? 'approved' : 'rejected';
    const adminNote = status === 'settled' ? "Audit Verified" : "Audit Rejected";
    
    if (!window.confirm(`Are you sure you want to ${finalStatus} this transaction?`)) return;

    try {
      setLoading(true);
      const targetId = String(id).trim();
      await adminAxios.put(`transactions/admin/verify-payment/${targetId}`, { 
        status: finalStatus, 
        note: adminNote,
        tenantId 
      });
      
      fetchData();
    } catch (err) {
      alert("COMMAND FAILURE: Verification could not be broadcasted.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const localStyles = {
    container: {
      padding: "40px",
      backgroundColor: "#060608", // Hardcoded dark background matching all other admin pages
      minHeight: "100vh",
      color: colors?.text || "#ffffff",
      fontFamily: "'Inter', sans-serif"
    },
    header: { marginBottom: "30px" },
    tabBar: {
      display: 'flex',
      gap: '20px',
      marginBottom: '30px',
      borderBottom: `1px solid ${colors?.border || "rgba(255,255,255,0.08)"}`
    },
    tab: (active) => ({
      padding: "12px 20px",
      cursor: "pointer",
      fontSize: "11px",
      fontWeight: "800",
      letterSpacing: "1.2px",
      color: active ? (colors?.brand || "#10b981") : "#64748b",
      borderBottom: active ? `2px solid ${colors?.brand || "#10b981"}` : "none",
      transition: "0.3s",
      textTransform: "uppercase"
    }),
    table: { width: "100%", borderCollapse: "separate", borderSpacing: "0 8px" },
    th: {
      padding: "15px",
      fontSize: "10px",
      textTransform: "uppercase",
      letterSpacing: "2px",
      color: colors?.brand || "#10b981",
      opacity: 0.7,
      textAlign: "left",
      fontWeight: "900"
    },
    tr: { backgroundColor: colors?.card || "rgba(255, 255, 255, 0.02)", transition: "0.2s" },
    td: { padding: "18px 15px", fontSize: "13px", borderBottom: `1px solid ${colors?.border || "rgba(255,255,255,0.05)"}` },
    statusBadge: (status) => ({
      padding: "4px 12px",
      borderRadius: "6px",
      fontSize: "9px",
      fontWeight: "900",
      textTransform: "uppercase",
      backgroundColor: status === 'approved' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
      color: status === 'approved' ? '#10b981' : '#ef4444',
      border: `1px solid ${status === 'approved' ? '#10b981' : '#ef4444'}`,
      letterSpacing: "0.5px"
    }),
    code: {
      background: "rgba(0, 0, 0, 0.3)",
      padding: "6px 10px",
      borderRadius: "4px",
      fontSize: "11px",
      fontFamily: "'JetBrains Mono', monospace",
      color: colors?.text || "#fff",
      border: `1px solid ${colors?.border || "rgba(255,255,255,0.1)"}`,
      wordBreak: "break-all"
    },
    approveBtn: {
      background: colors?.brand || "#10b981",
      color: "#000",
      border: "none",
      padding: "10px 18px",
      borderRadius: "6px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "10px",
      marginRight: "8px",
      transition: "0.2s"
    },
    rejectBtn: {
      background: "transparent",
      color: "#ef4444",
      border: "1px solid #ef4444",
      padding: "10px 18px",
      borderRadius: "6px",
      cursor: "pointer",
      fontWeight: "900",
      fontSize: "10px",
      transition: "0.2s"
    }
  };

  const dataToDisplay = activeTab === "pending" ? requests : history;

  return (
    <div style={localStyles.container} data-tenant-scope={tenantId}>
      <div style={localStyles.header}>
        <h2 style={{ margin: 0, fontWeight: "900", letterSpacing: "3px", textTransform: "uppercase", fontSize: '22px', color: "#ffffff" }}>
  Payment <span style={{ color: colors?.brand || "#10b981" }}>Audit Terminal</span>
</h2>
        <p style={{ margin: "5px 0 0", fontSize: "12px", color: "#64748b", fontWeight: "600" }}>System Time: {new Date().getFullYear()}</p>
      </div>

      <div style={localStyles.tabBar}>
        <div style={localStyles.tab(activeTab === "pending")} onClick={() => setActiveTab("pending")}>
          Awaiting Review ({requests.length})
        </div>
        <div style={localStyles.tab(activeTab === "history")} onClick={() => setActiveTab("history")}>
          Audit History
        </div>
      </div>

      {error && (
        <div style={{ padding: "15px", backgroundColor: "rgba(239, 68, 68, 0.1)", border: "1px solid #ef4444", color: '#ef4444', marginBottom: '20px', fontWeight: 'bold', borderRadius: '8px', fontSize: '12px' }}>
          <i className="fas fa-exclamation-triangle" style={{ marginRight: '10px' }}></i> {error}
        </div>
      )}

      {loading && dataToDisplay.length === 0 ? (
        <div style={{ textAlign: "center", padding: "100px", color: colors?.brand || "#10b981" }}>
          <div className="audit-loader"></div>
          <p style={{ letterSpacing: "4px", fontWeight: "900", fontSize: '11px', marginTop: '20px' }}>SYNCHRONIZING SECURE LEDGER...</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }} className="custom-scroll">
          <table style={localStyles.table}>
            <thead>
              <tr>
                <th style={localStyles.th}>Node Origin</th>
                <th style={localStyles.th}>Credit Value</th>
                <th style={localStyles.th}>TXID / Hash</th>
                <th style={localStyles.th}>{activeTab === "pending" ? "Authorization" : "Audit Status"}</th>
              </tr>
            </thead>
            <tbody>
              {dataToDisplay.length > 0 ? dataToDisplay.map((req) => (
                <tr key={String(req._id)} style={localStyles.tr}>
                  <td style={localStyles.td}>
                    <div style={{ fontWeight: "800", fontSize: '14px' }}>{req.user?.name || "Anonymous Node"}</div>
                    <div style={{ fontSize: "10px", opacity: 0.5, letterSpacing: '0.5px' }}>{req.user?.email || "ID: " + req.user?._id}</div>
                  </td>
                  <td style={localStyles.td}>
                    <span style={{ color: colors?.brand || "#10b981", fontWeight: "900", fontSize: '15px' }}>
                      {req.amount?.toLocaleString()} <small style={{ fontSize: '10px' }}>{req.currency || "INR"}</small>
                    </span>
                  </td>
                  <td style={localStyles.td}>
                    <code style={localStyles.code}>{req.txid || "NO_HASH_FOUND"}</code>
                  </td>
                  <td style={localStyles.td}>
                    {activeTab === "pending" ? (
                      <div style={{ display: 'flex' }}>
                        <button 
                          disabled={loading}
                          style={localStyles.approveBtn} 
                          onClick={() => handleAction(req._id, 'settled')}
                        >
                          APPROVE
                        </button>
                        <button 
                          disabled={loading}
                          style={localStyles.rejectBtn} 
                          onClick={() => handleAction(req._id, 'rejected')}
                        >
                          REJECT
                        </button>
                      </div>
                    ) : (
                      <span style={localStyles.statusBadge(req.status)}>{req.status}</span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" style={{ padding: "100px", textAlign: "center", opacity: 0.3 }}>
                    <i className="fas fa-folder-open" style={{ fontSize: '30px', marginBottom: '15px', display: 'block' }}></i>
                    <span style={{ letterSpacing: '2px', fontWeight: 'bold', fontSize: '11px' }}>SECTOR CLEAR - NO DATA FOUND</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PaymentAudit;